import { Modal as AmelioModal } from '@amelio/ui-library';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { type ActionMenuItem } from './ActionMenu';
import { CompetenciesHero } from './CompetenciesHero';
import { CompetenciesTabs, type CompetenciesTab } from './CompetenciesTabs';
import { CompetenciesToolbar } from './CompetenciesToolbar';
import { CompetencyGroupSection } from './CompetencyGroupSection';
import { AssignedProfilesModal } from './employees/AssignedProfilesModal';
import { AssignToEmployeesWizard } from './employees/AssignToEmployeesWizard';
import {
    ArrowRightIcon,
    EyeIcon,
    PencilIcon,
    PlusIcon,
    PowerIcon,
    SearchIcon,
    Trash2Icon,
    UserPlusIcon,
} from './icons';
import { ProficiencyLevelsTab } from './levels/ProficiencyLevelsTab';
import { CompetencyProfilesTab } from './profiles/CompetencyProfilesTab';
import { CompetencyTranslationsTab } from './translations/CompetencyTranslationsTab';

import type { CompetencyStatusFilter } from '../types';
import type {
    Competency,
    CompetencyGroup,
    CompetencyStatusType,
} from '@/types/competency/Competency';
import type { CompetencyProfileItem } from '@/types/competency/CompetencyProfile';

import {
    useGetCompetencyProfiles,
    useUpdateCompetencyProfile,
} from '@/api/competency-services/competency-profile';
import {
    useAddEmployeeCompetency,
    useEmployeesWithCompetency,
} from '@/api/competency-services/employee-competency';
import { useGetRoster } from '@/api/competency-services/profile-assignment';
import { useGetUserPermissions } from '@/api/directory';
import { userInstanceAtom } from '@/atoms/userDetails';
import { Permission } from '@/constants/Permission';
import { ProficiencyLevel } from '@/constants/ProficiencyLevel';
import { useAmelioTranslations } from '@/hooks';
import { useUserPermissions } from '@/hooks/api/useUserPermissions';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { scrollPageToTop } from '@/navigation/ScrollToTop';
import { showErrorToast, showWarningToast } from '@/plugins/Toast';
import { CompetencyStatus } from '@/types/competency/Competency';
import { getTextByLanguage } from '@/utils/localization';

type CompetenciesProps = {
    competencyGroups: CompetencyGroup[];
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
    onAddGroup: () => void;
    onAddCompetency: (groupId?: string) => void;
    onExplore: () => void;
    onEditGroup: (group: CompetencyGroup) => void;
    onToggleGroupStatus: (group: CompetencyGroup) => void;
    onDeleteGroup: (group: CompetencyGroup) => void;
    onEditCompetency: (competency: Competency, group: CompetencyGroup) => void;
    onMoveCompetency: (competency: Competency) => void;
    onToggleCompetencyStatus: (competency: Competency) => void;
    onDeleteCompetency: (competency: Competency) => void;
};

export const Competencies = ({
    competencyGroups,
    isLoading,
    isError,
    onRetry,
    onAddGroup,
    onAddCompetency,
    onExplore,
    onEditGroup,
    onToggleGroupStatus,
    onDeleteGroup,
    onEditCompetency,
    onMoveCompetency,
    onToggleCompetencyStatus,
    onDeleteCompetency,
}: CompetenciesProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const history = useHistory();
    const location = useLocation();
    const isAmelioAdmin = useAtomValue(userInstanceAtom).isAmelioAdmin;

    const [activeTab, setActiveTab] = useState<CompetenciesTab>(() => {
        const tab = new URLSearchParams(window.location.search).get('tab');

        return tab === 'profiles' || tab === 'levels' || tab === 'translations'
            ? tab
            : 'competencies';
    });

    const handleTabChange = (tab: CompetenciesTab) => {
        setActiveTab(tab);
        const params = new URLSearchParams(location.search);
        params.set('tab', tab);
        history.replace({ search: params.toString() });
        scrollPageToTop();
    };
    const [openGroupIds, setOpenGroupIds] = useState<Set<string>>(new Set());
    const [openCompetencyIds, setOpenCompetencyIds] = useState<Set<string>>(() => new Set());
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<CompetencyStatusFilter>('all');
    const isSearching = searchText.trim().length > 0 || statusFilter !== 'all';
    const wasSearching = useRef(false);
    const [competencyToAssignToEmployees, setCompetencyToAssignToEmployees] =
        useState<Competency | null>(null);
    const [groupToAssign, setGroupToAssign] = useState<CompetencyGroup | null>(null);
    const [competencyForAssignedProfiles, setCompetencyForAssignedProfiles] =
        useState<Competency | null>(null);

    const { mutateAsync: addEmployeeCompetency, isPending: isAddingEmployeeCompetency } =
        useAddEmployeeCompetency();

    const { data: permissions } = useGetUserPermissions();
    const canAssignCompetencyProfiles = permissions?.canAssignCompetencyProfiles ?? false;

    // The assigned-profiles modal drills into the employees behind each profile, so it reads
    // employee data and needs the performance-profile permission on top of competency
    // administration. Same requirement as the Profiles tab (#14812); this menu was missed
    // there and still offered the option to a user who cannot load it (#15188).
    const { can } = useUserPermissions();
    const canViewAssignedProfiles = can(
        Permission.ViewPerformanceProfile,
        Permission.ManageCompetencies,
    );
    const canUseAssignmentFeatures = canAssignCompetencyProfiles && !isAmelioAdmin;

    const { data: roster = [] } = useGetRoster(
        { isActiveOnly: true, includeInvited: true },
        canUseAssignmentFeatures,
    );

    const { data: competencyProfiles } = useGetCompetencyProfiles(canUseAssignmentFeatures);
    const { mutateAsync: updateCompetencyProfile, isPending: isUpdatingCompetencyProfile } =
        useUpdateCompetencyProfile();

    const resolveAlreadyAssignedIds = useEmployeesWithCompetency(
        competencyToAssignToEmployees?.id ?? '',
    );
    const competencyById = useMemo(() => {
        const entries = competencyGroups.flatMap((group) =>
            group.competencies.map((competency) => [competency.id, competency] as const),
        );

        return new Map(entries);
    }, [competencyGroups]);

    const addCompetenciesToProfiles = async (competencies: Competency[], profileIds: string[]) => {
        const profilesById = new Map(
            (competencyProfiles ?? []).map((profile) => [profile.id, profile]),
        );

        for (const profileId of profileIds) {
            const profile = profilesById.get(profileId);
            if (!profile) {
                continue;
            }

            const newItems: CompetencyProfileItem[] = competencies
                .filter(
                    (competency) =>
                        !profile.items.some((item) => item.competencyId === competency.id),
                )
                .map((competency) => ({
                    competencyId: competency.id,
                    expectedLevel: ProficiencyLevel.SATISFACTORY,
                    isPriority: false,
                }));

            if (newItems.length === 0) {
                continue;
            }

            await updateCompetencyProfile({
                id: profile.id,
                payload: {
                    name: profile.name,
                    description: profile.description,
                    sortOrder: profile.sortOrder,
                    items: [...profile.items, ...newItems],
                },
            });
        }
    };

    const addCompetencyToProfiles = async (profileIds: string[]) => {
        if (!competencyToAssignToEmployees) {
            return;
        }

        const currentCompetency = competencyById.get(competencyToAssignToEmployees.id);
        if (!currentCompetency || currentCompetency.status !== CompetencyStatus.ACTIVE) {
            showErrorToast({ message: t('competencies.actions.competencyDeactivatedTooltip') });
            setCompetencyToAssignToEmployees(null);
            return;
        }

        await addCompetenciesToProfiles([currentCompetency], profileIds);
    };

    const activeGroupCompetencies = useMemo(
        () =>
            (
                (
                    groupToAssign &&
                    (competencyGroups.find((group) => group.id === groupToAssign.id) ??
                        groupToAssign)
                )?.competencies ?? []
            ).filter((competency) => competency.status === CompetencyStatus.ACTIVE),
        [competencyGroups, groupToAssign],
    );

    const ensureEmployeesHaveProfilesOrWarn = (employeeIds: string[], onClear: () => void) => {
        const selectedEmployees = roster.filter((employee) => employeeIds.includes(employee.id));
        const employeesWithoutProfile = selectedEmployees.filter(
            (employee) => !employee.assignedProfileId,
        );

        if (employeesWithoutProfile.length > 0) {
            showWarningToast({
                message: t('managerAssessmentSetup.employeeHasNoCompetencyProfile'),
            });
            onClear();
            // Signal a non-fatal, user-facing validation issue to the wizard: reject the
            // confirmation so it does not show a success toast or close, but use the
            // isDemoRestriction escape hatch so useError.handleError is a no-op.
            throw { isDemoRestriction: true };
        }
    };

    const assignCompetencyToEmployees = async (employeeIds: string[]) => {
        if (!competencyToAssignToEmployees) {
            return;
        }

        ensureEmployeesHaveProfilesOrWarn(employeeIds, () =>
            setCompetencyToAssignToEmployees(null),
        );

        const currentCompetency = competencyById.get(competencyToAssignToEmployees.id);
        if (!currentCompetency || currentCompetency.status !== CompetencyStatus.ACTIVE) {
            showErrorToast({ message: t('competencies.actions.competencyDeactivatedTooltip') });
            setCompetencyToAssignToEmployees(null);
            return;
        }

        await addEmployeeCompetency({ competencyId: currentCompetency.id, employeeIds });
    };

    const assignGroupToEmployees = async (employeeIds: string[]) => {
        if (activeGroupCompetencies.length < 1) {
            showErrorToast({ message: t('competencies.assignGroup.noCompetenciesTooltip') });
            setGroupToAssign(null);
            return;
        }

        ensureEmployeesHaveProfilesOrWarn(employeeIds, () => setGroupToAssign(null));

        for (const competency of activeGroupCompetencies) {
            await addEmployeeCompetency({ competencyId: competency.id, employeeIds });
        }
    };

    const toggleGroup = (groupId: string) =>
        setOpenGroupIds((current) => {
            if (isSearching) {
                const next = new Set(current);
                if (next.has(groupId)) {
                    next.delete(groupId);
                } else {
                    next.add(groupId);
                }
                return next;
            }
            return new Set(current.has(groupId) ? [] : [groupId]);
        });

    const toggleCompetency = (competencyId: string) =>
        setOpenCompetencyIds((current) => {
            const next = new Set(current);
            if (next.has(competencyId)) {
                next.delete(competencyId);
            } else {
                next.add(competencyId);
            }

            return next;
        });

    const handleAssignToEmployees = (competency: Competency) => {
        if (competency.status !== CompetencyStatus.ACTIVE) {
            showErrorToast({ message: t('competencies.actions.competencyDeactivatedTooltip') });
            return;
        }

        setCompetencyToAssignToEmployees(competency);
    };

    const filteredGroups = useMemo(() => {
        const search = searchText.trim().toLowerCase();

        const matchesStatus = (status: CompetencyStatusType): boolean => {
            if (statusFilter === 'active') {
                return status === CompetencyStatus.ACTIVE;
            }

            if (statusFilter === 'inactive') {
                return status !== CompetencyStatus.ACTIVE;
            }

            return true;
        };

        const compareByName = (a: string, b: string): number =>
            a.localeCompare(b, undefined, { sensitivity: 'base' });

        return competencyGroups
            .filter((group) => matchesStatus(group.status))
            .map((group) => {
                const groupName = getTextByLanguage(group.name, language).toLowerCase();
                const groupDescription = getTextByLanguage(
                    group.description,
                    language,
                ).toLowerCase();
                const groupMatches =
                    !search || groupName.includes(search) || groupDescription.includes(search);

                const competencies = group.competencies
                    .filter((competency) => {
                        if (!matchesStatus(competency.status)) {
                            return false;
                        }

                        if (!search || groupMatches) {
                            return true;
                        }

                        const name = getTextByLanguage(competency.name, language).toLowerCase();
                        const description = getTextByLanguage(
                            competency.description,
                            language,
                        ).toLowerCase();

                        return name.includes(search) || description.includes(search);
                    })
                    .sort((a, b) =>
                        compareByName(
                            getTextByLanguage(a.name, language),
                            getTextByLanguage(b.name, language),
                        ),
                    );

                return { group, competencies, groupMatches };
            })
            .filter(({ competencies, groupMatches }) => groupMatches || competencies.length > 0)
            .sort((a, b) =>
                compareByName(
                    getTextByLanguage(a.group.name, language),
                    getTextByLanguage(b.group.name, language),
                ),
            )
            .map(({ group, competencies }) => ({
                ...group,
                competencies,
            }));
    }, [competencyGroups, searchText, statusFilter, language]);

    useEffect(() => {
        if (isSearching && !wasSearching.current) {
            setOpenGroupIds(new Set(filteredGroups.map((group) => group.id)));
        }
        wasSearching.current = isSearching;
    }, [isSearching, filteredGroups]);

    const groupActions = (group: CompetencyGroup): ActionMenuItem[] => {
        const fullGroup = competencyGroups.find((candidate) => candidate.id === group.id) ?? group;

        const isGroupInactive = fullGroup.status !== CompetencyStatus.ACTIVE;
        const hasActiveCompetencies = fullGroup.competencies.some(
            (competency) => competency.status === CompetencyStatus.ACTIVE,
        );

        return [
            {
                id: 'add-competency',
                label: t('competencies.emptyGroupCta'),
                icon: <PlusIcon className={'h-4 w-4'} />,
                onClick: () => onAddCompetency(fullGroup.id),
            },
            ...(canUseAssignmentFeatures
                ? [
                      {
                          id: 'assign-group',
                          label: t('competencies.assignGroup.action'),
                          icon: <UserPlusIcon className={'h-4 w-4'} />,
                          disabled: isGroupInactive || !hasActiveCompetencies,
                          tooltip: isGroupInactive
                              ? t('competencies.actions.groupDeactivatedTooltip')
                              : !hasActiveCompetencies
                                ? t('competencies.assignGroup.noCompetenciesTooltip')
                                : undefined,
                          onClick: () => setGroupToAssign(fullGroup),
                      },
                  ]
                : []),
            {
                id: 'edit',
                label: t('common.edit'),
                icon: <PencilIcon className={'h-4 w-4'} />,
                onClick: () => onEditGroup(fullGroup),
            },
            {
                id: 'toggle-status',
                label:
                    fullGroup.status === CompetencyStatus.ACTIVE
                        ? t('common.deactivate')
                        : t('common.activate'),
                icon: <PowerIcon className={'h-4 w-4'} />,
                onClick: () => onToggleGroupStatus(fullGroup),
            },
            {
                id: 'delete',
                label: t('common.delete'),
                icon: <Trash2Icon className={'h-4 w-4'} />,
                danger: true,
                onClick: () => onDeleteGroup(fullGroup),
            },
        ];
    };

    const competencyActions = (
        competency: Competency,
        group: CompetencyGroup,
    ): ActionMenuItem[] => [
        {
            id: 'edit',
            label: t('common.edit'),
            icon: <PencilIcon className={'h-4 w-4'} />,
            onClick: () => onEditCompetency(competency, group),
        },
        ...(!isAmelioAdmin && canViewAssignedProfiles
            ? [
                  {
                      id: 'view-assigned-profiles',
                      label: t('competencies.actions.viewAssignedProfiles'),
                      icon: <EyeIcon className={'h-4 w-4'} />,
                      onClick: () => setCompetencyForAssignedProfiles(competency),
                  },
              ]
            : []),
        {
            id: 'move',
            label: t('competencies.actions.move'),
            icon: <ArrowRightIcon className={'h-4 w-4'} />,
            onClick: () => onMoveCompetency(competency),
        },
        {
            id: 'toggle-status',
            label:
                competency.status === CompetencyStatus.ACTIVE
                    ? t('common.deactivate')
                    : t('common.activate'),
            icon: <PowerIcon className={'h-4 w-4'} />,
            // Activating a competency whose group is inactive also reactivates the group, so the
            // action stays enabled even then; only edit/move/delete rely on the group being active.
            onClick: () => onToggleCompetencyStatus(competency),
        },
        {
            id: 'delete',
            label: t('common.delete'),
            icon: <Trash2Icon className={'h-4 w-4'} />,
            danger: true,
            onClick: () => onDeleteCompetency(competency),
        },
    ];

    const hasGroups = competencyGroups.length > 0;
    const hasResults = filteredGroups.length > 0;

    return (
        <>
            <main className={'cmp-page-main max-w-competencies mx-auto flex flex-col px-4 pt-16'}>
                <CompetenciesHero />

                <CompetenciesTabs
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />

                {activeTab === 'competencies' && (
                    // No backdrop-blur: it would re-anchor the nested DragOverlay to this wrapper instead of the viewport.
                    <div className={'cmp-card-pad rounded-2xl border border-white bg-white/60'}>
                        <CompetenciesToolbar
                            searchText={searchText}
                            statusFilter={statusFilter}
                            onSearchChange={setSearchText}
                            onStatusFilterChange={setStatusFilter}
                            onAddGroup={onAddGroup}
                            onAddCompetency={onAddCompetency}
                            onExplore={onExplore}
                        />

                        <div className={'bg-base-200 my-12 h-px'} />

                        {isLoading && (
                            <p className={'py-6 text-center text-sm text-gray-600'}>
                                {t('competencies.emptyState')}
                            </p>
                        )}

                        {!isLoading && isError && (
                            <div
                                className={
                                    'flex flex-col items-center justify-center py-12 text-center'
                                }
                            >
                                <p className={'text-error text-sm'}>{t('competencies.error')}</p>

                                <button
                                    type={'button'}
                                    onClick={onRetry}
                                    className={
                                        'text-navy hover:bg-base-25 mt-4 flex h-11 items-center rounded-lg border border-gray-200 bg-white px-4 text-sm font-medium transition-colors'
                                    }
                                    data-testid={'competencies-retry'}
                                >
                                    {t('competencies.retry')}
                                </button>
                            </div>
                        )}

                        {!isLoading && !isError && !hasGroups && (
                            <p className={'py-6 text-center text-sm text-gray-600'}>
                                {t('competencies.emptyState')}
                            </p>
                        )}

                        {!isLoading && !isError && hasGroups && !hasResults && (
                            <div
                                className={
                                    'flex flex-col items-center justify-center py-12 text-center'
                                }
                            >
                                <SearchIcon className={'mb-3 h-10 w-10 text-gray-600/40'} />

                                <p className={'text-sm text-gray-600'}>
                                    {t('competencies.noResults')}
                                </p>
                            </div>
                        )}

                        {!isLoading && !isError && hasResults && (
                            <div className={'space-y-8 md:space-y-12'}>
                                {filteredGroups.map((group) => (
                                    <CompetencyGroupSection
                                        key={group.id}
                                        group={group}
                                        isOpen={openGroupIds.has(group.id)}
                                        openCompetencyIds={openCompetencyIds}
                                        onToggleGroup={() => toggleGroup(group.id)}
                                        onToggleCompetency={toggleCompetency}
                                        onAddCompetency={onAddCompetency}
                                        onAssignToEmployees={handleAssignToEmployees}
                                        groupActions={groupActions(group)}
                                        competencyActions={competencyActions}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'profiles' && (
                    <CompetencyProfilesTab competencyGroups={competencyGroups} />
                )}

                {activeTab === 'levels' && <ProficiencyLevelsTab />}

                {activeTab === 'translations' && <CompetencyTranslationsTab />}
            </main>

            <AmelioModal
                isOpen={Boolean(competencyToAssignToEmployees)}
                onClose={() => setCompetencyToAssignToEmployees(null)}
                className={'w-[896px] max-w-[92vw]'}
                dataTestId={'competency-assign-employees-modal'}
                zIndex={50}
            >
                {competencyToAssignToEmployees && (
                    <AssignToEmployeesWizard
                        title={t('competencies.assignToEmployees.title', {
                            name: getTextByLanguage(competencyToAssignToEmployees.name, language),
                        })}
                        subtitle={t('competencies.assignToEmployees.subtitle')}
                        competencyName={getTextByLanguage(
                            competencyToAssignToEmployees.name,
                            language,
                        )}
                        confirmLabel={(count) =>
                            t('competencies.assignToEmployees.actions.confirm', { count })
                        }
                        confirmProfileLabel={(count) =>
                            t('competencies.assignToEmployees.actions.confirmProfile', { count })
                        }
                        successMessage={(count) =>
                            t('competencies.assignToEmployees.review.success', { count })
                        }
                        successProfileMessage={(count) =>
                            t('competencies.assignToEmployees.review.successProfile', { count })
                        }
                        isSubmitting={isAddingEmployeeCompetency || isUpdatingCompetencyProfile}
                        resolveAlreadyAssignedIds={resolveAlreadyAssignedIds}
                        competencyId={competencyToAssignToEmployees?.id}
                        disabledEmployeeLabel={t(
                            'competenciesTab.competencies.assignModal.individual.alreadyAssigned',
                        )}
                        onConfirm={assignCompetencyToEmployees}
                        onConfirmProfileMethod={addCompetencyToProfiles}
                        onClose={() => setCompetencyToAssignToEmployees(null)}
                    />
                )}
            </AmelioModal>

            <AmelioModal
                isOpen={Boolean(groupToAssign)}
                onClose={() => setGroupToAssign(null)}
                className={'w-[896px] max-w-[92vw]'}
                dataTestId={'competency-assign-group-modal'}
                zIndex={50}
            >
                {groupToAssign && (
                    <AssignToEmployeesWizard
                        title={t('competencies.assignGroup.title', {
                            name: getTextByLanguage(groupToAssign.name, language),
                        })}
                        subtitle={t('competencies.assignGroup.subtitle', {
                            count: activeGroupCompetencies.length,
                        })}
                        competencyName={getTextByLanguage(groupToAssign.name, language)}
                        confirmLabel={(count) =>
                            t('competencies.assignToEmployees.actions.confirm', { count })
                        }
                        confirmProfileLabel={(count) =>
                            t('competencies.assignToEmployees.actions.confirmProfile', { count })
                        }
                        successMessage={(count) => t('competencies.assignGroup.success', { count })}
                        successProfileMessage={(count) =>
                            t('competencies.assignGroup.successProfile', { count })
                        }
                        isSubmitting={isAddingEmployeeCompetency || isUpdatingCompetencyProfile}
                        groupId={groupToAssign?.id}
                        disabledEmployeeLabel={t(
                            'competenciesTab.assignGroup.assignModal.individual.alreadyAssigned',
                        )}
                        onConfirm={assignGroupToEmployees}
                        onConfirmProfileMethod={(profileIds) =>
                            addCompetenciesToProfiles(activeGroupCompetencies, profileIds)
                        }
                        onClose={() => setGroupToAssign(null)}
                    />
                )}
            </AmelioModal>

            {competencyForAssignedProfiles && (
                <AssignedProfilesModal
                    competency={competencyForAssignedProfiles}
                    onClose={() => setCompetencyForAssignedProfiles(null)}
                />
            )}
        </>
    );
};
